/**
 * The console client of the platform administrator routes of library rights in release-engine
 * (2026-10-12-shared-libraries, WS-13, R-018, R-030): the queue and the decision.
 *
 * Both routes sit behind `cp:library-rights:decide`, a site-wide permission. The app routes of
 * `libraryRights.api.ts` sit behind `cp:app:read` on one app. The two halves live in two modules, so
 * that each pane imports the module whose permission gates it (`permission-gate-parity.test.ts`).
 *
 * Who calls it: `LibraryRightsQueuePane` and `LibraryRightDecisionPanel`. The decision goes through
 * `useStepUp()`. Every call keeps the rules of `libraryRights.api.ts`: an encoded path id, the step-up
 * header from `stepUpHeaders`, the release-engine body keys alone, `ApiError` on a non-2xx answer, and
 * no retry, cache or store.
 *
 * The queue query and the decision body are the kernel-rights ones (release-engine binds
 * `kernelRightDecisionBodySchema` to the decision, WS-3 decision 7). So the page size and the offset
 * cap come from `kernelRightsQueue.api.ts` (index default DEF-2), and the decision body is
 * `KernelRightDecisionBody`. `libraryRights.wire-parity.test.ts` holds both equal to
 * `release-engine/openapi.json`.
 */
import type { CpPermission, LibraryRightRoute, Sha256Digest } from '@zion/sdk-contracts';
import type { LibraryRightRequestView } from './libraryRights.api';
import {
  KERNEL_RIGHT_QUEUE_LIMIT,
  KERNEL_RIGHT_QUEUE_MAX_OFFSET,
  type KernelRightDecisionBody,
} from './kernelRightsQueue.api';
import { stepUpHeaders } from '../platform/auth/step-up';
import { api, apiFetch } from '../platform/http/client';

/**
 * The check of the queue and of the decision: `cp:library-rights:decide`, site-wide (D8 of
 * 2026-10-20-cp-permissioning; it replaced the marker `cp:platform:admin`). `platform_admin` holds
 * it, and no app grant carries it.
 */
export const LIBRARY_RIGHTS_DECIDE_PERMISSION = 'cp:library-rights:decide' satisfies CpPermission;

/** The four states of a library right request, in the order of the queue segments. */
export const LIBRARY_RIGHT_REQUEST_STATUSES = ['pending', 'approved', 'denied', 'revoked'] as const;

/** One state of a library right request. `pending` moves to `approved` or `denied`; `approved` moves to `revoked`. */
export type LibraryRightRequestStatus = (typeof LIBRARY_RIGHT_REQUEST_STATUSES)[number];

/** The page size that the console asks of the queue: the largest that the queue query admits (200). */
export const LIBRARY_RIGHT_QUEUE_LIMIT = KERNEL_RIGHT_QUEUE_LIMIT;

/** The largest offset that the queue query admits (10,000). */
export const LIBRARY_RIGHT_QUEUE_MAX_OFFSET = KERNEL_RIGHT_QUEUE_MAX_OFFSET;

/**
 * What one approval key binds, for the platform administrator who decides (R-006, R-030 item 4).
 * `routes` and `inboundTopics` are both `null` when no library version declares the binding any more.
 */
export interface LibraryRightBindingView {
  readonly name: string;
  readonly bindingDigest: Sha256Digest;
  readonly routes: readonly LibraryRightRoute[] | null;
  readonly inboundTopics: readonly string[] | null;
}

/** The kind of the requester row: an app, or a library that uses another library (R-033). */
export type LibraryRightRequesterKind = 'app' | 'library' | 'instance';

/**
 * One entry of the platform administrator's queue: the request, the requester's slug, name and kind,
 * and one binding per approval key, in the order of `rights`.
 */
export interface LibraryRightQueueEntry extends LibraryRightRequestView {
  readonly requesterSlug: string;
  readonly requesterName: string;
  readonly requesterKind: LibraryRightRequesterKind;
  readonly bindings: readonly LibraryRightBindingView[];
}

/** The body of the decision route. An approve may omit the note; a deny and a revoke must carry one. */
export type LibraryRightDecisionBody = KernelRightDecisionBody;

/**
 * The path of one queue page: `/v1/admin/library-rights/requests?status=<status>&limit=200`, and
 * `&offset=<offset>` when the offset is above 0. Pure.
 *
 * @param offset the count of entries before the page, an integer from 0 to
 *   {@link LIBRARY_RIGHT_QUEUE_MAX_OFFSET}. The default 0 is the first page.
 * @throws Error('invalid queue offset') for any other offset, so the console never sends a query that
 *   the release-engine pipe refuses with 400.
 */
export function libraryRightQueuePath(status: LibraryRightRequestStatus, offset = 0): string {
  if (!Number.isInteger(offset) || offset < 0 || offset > LIBRARY_RIGHT_QUEUE_MAX_OFFSET) {
    throw new Error('invalid queue offset');
  }
  const query = new URLSearchParams({ status, limit: String(LIBRARY_RIGHT_QUEUE_LIMIT) });
  if (offset > 0) query.set('offset', String(offset));
  return `/v1/admin/library-rights/requests?${query.toString()}`;
}

/**
 * Read one page of the queue in one status: at most {@link LIBRARY_RIGHT_QUEUE_LIMIT} entries, oldest
 * first, after the first `offset` entries (`LibraryRightsAdminController.queue`).
 *
 * @throws Error('invalid queue offset') before any request for an offset out of range; ApiError on a
 *   non-2xx answer (403 with no `cp:library-rights:decide`).
 */
export async function listLibraryRightQueue(status: LibraryRightRequestStatus, offset = 0): Promise<readonly LibraryRightQueueEntry[]> {
  return api.get<readonly LibraryRightQueueEntry[]>(libraryRightQueuePath(status, offset));
}

/**
 * Approve, deny or revoke a request: `POST /v1/admin/library-rights/requests/:id/decision`. The body
 * carries `decision`, and `note` only when the caller gave one.
 *
 * @param stepUpId `null` on the first send; the approved step-up request id on the second.
 * @returns the request after the decision (200).
 * @throws Error('invalid step-up request id') before any request for a malformed id; ApiError on a
 *   non-2xx answer: 400 from the pipe, 403 `step_up_required` on a first send, 403
 *   `self_decision_refused` for an approve by the asker, 404 for an unknown request, 409 for a request
 *   in another state or 409 `libraries_not_accepted` while `LIBRARY_MIN_DP_VERSION` is unset, 503
 *   `code_signing_unavailable` or `step_up_unavailable`.
 */
export async function decideLibraryRightRequest(
  requestId: string,
  body: LibraryRightDecisionBody,
  stepUpId: string | null,
): Promise<LibraryRightRequestView> {
  const wire = body.note === undefined ? { decision: body.decision } : { decision: body.decision, note: body.note };
  return apiFetch<LibraryRightRequestView>(`/v1/admin/library-rights/requests/${encodeURIComponent(requestId)}/decision`, {
    method: 'POST',
    body: JSON.stringify(wire),
    headers: stepUpHeaders(stepUpId),
  });
}
